import { ResponseError } from "@wingspanhq/wingspan-api-sdk";
import {
  useWSInfiniteQuery,
  WSInfiniteQueryConfig,
  WSInfiniteQueryResult,
  WSQueryKey
} from "@ws-react-query";
import { SdkEndpointMethod, V3QueryMethodName } from "./createV3QueryHook";

/** The v3 cursor page parameter (`{ size?, token? }`) shared by all list endpoints. */
type V3PageParameter = { size?: number; token?: string };

/** The v3 cursor metadata returned on every list response. */
type V3Paginated = { pagination?: { nextPageToken?: string } };

/**
 * Paginated-query arguments, mirroring the SDK request optionality. Token
 * pagination is handled internally, so the caller passes the same request as
 * `useQuery` — the per-page cursor token is merged into `request.page`.
 */
type V3PaginatedQueryArgs<TRequest, TResponse> = undefined extends TRequest
  ? [
      request?: TRequest,
      config?: WSInfiniteQueryConfig<TResponse, ResponseError>
    ]
  : [
      request: TRequest,
      config?: WSInfiniteQueryConfig<TResponse, ResponseError>
    ];

/**
 * The paginated-query hook produced by {@link createV3PaginatedQueryHook}: pages of
 * the SDK list response, walked via the v3 `{ size, token }` cursor.
 */
export type V3PaginatedQueryHook<TRequest, TResponse> = (
  ...args: V3PaginatedQueryArgs<TRequest, TResponse>
) => WSInfiniteQueryResult<TResponse, ResponseError>;

/** Resolves the typed paginated hook for a given SDK method type. */
export type V3PaginatedQueryHookFor<TMethod> = TMethod extends (
  request: infer TRequest,
  ...rest: any[]
) => Promise<infer TResponse>
  ? V3PaginatedQueryHook<TRequest, TResponse>
  : never;

/**
 * Build a fully-typed paginated React Query hook from an SDK API
 * instance + a `list*` method name. The sibling of {@link createV3QueryHook} for
 * the v3 collections that accept a `{ size, token }` page parameter and return a
 * `{ data, pagination }` response.
 *
 * The generated hook mirrors the SDK call 1:1 (same request type & optionality,
 * same response model per page) and drives the cursor automatically: it merges
 * the per-page token into `request.page` and defaults `getNextPageParam` to
 * follow `pagination.nextPageToken`. Any `WSInfiniteQueryConfig` field — incl. a
 * custom `getNextPageParam` — is forwarded and overrides the default.
 *
 * Cache helpers (`getQueryKey` / `setQueryData`) are composed by {@link createV3Queries},
 * mirroring how it composes them around {@link createV3QueryHook} for reads.
 *
 * @param api        An instantiated SDK API client, e.g. `accountsApi`.
 * @param methodName A paginated `list*` method on `api`.
 * @param queryKey   The cache key base; the request is appended →
 *                   `[...queryKey, request]`.
 *
 * @example
 * const usePaginatedAccounts = createV3PaginatedQueryHook(
 *   accountsApi,
 *   "listAccounts",
 *   [QUERY_V3_ACCOUNTS, "paginated"]
 * );
 * // → usePaginatedAccounts(request?, config?) : WSInfiniteQueryResult<AccountList, …>
 * // data.pages: AccountList[]
 */
export function createV3PaginatedQueryHook<
  TApi extends object,
  TName extends V3QueryMethodName<TApi>
>(
  api: TApi,
  methodName: TName,
  queryKey: WSQueryKey
): V3PaginatedQueryHookFor<TApi[TName]> {
  const baseKey = Array.isArray(queryKey) ? queryKey : [queryKey];
  const callEndpoint = (api[methodName] as unknown as SdkEndpointMethod).bind(
    api
  );

  const useGeneratedPaginatedQuery = (
    request?: { page?: V3PageParameter },
    config?: WSInfiniteQueryConfig<unknown, ResponseError>
  ): WSInfiniteQueryResult<unknown, ResponseError> =>
    useWSInfiniteQuery<unknown, ResponseError>(
      [...baseKey, request],
      ({ pageParam }) =>
        callEndpoint({
          ...request,
          // Only merge the cursor token once we actually have one. On the first
          // page `pageParam` is undefined, and a nested `token: undefined` is
          // serialized by the SDK runtime as the literal string `page[token]=undefined`,
          // which stricter list endpoints (e.g. listPayers) reject with a 422.
          page:
            pageParam === undefined
              ? request?.page
              : { ...request?.page, token: pageParam }
        }),
      {
        getNextPageParam: (lastPage: unknown) =>
          (lastPage as V3Paginated)?.pagination?.nextPageToken || undefined,
        ...config
      }
    );

  return useGeneratedPaginatedQuery as unknown as V3PaginatedQueryHookFor<
    TApi[TName]
  >;
}
